import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, Pressable, ScrollView, TextInput } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import {
    selectedJobAtom,
    selectedVersionAtom,
} from '../store/company';
import { pNum, fNum, loadUserDataRaw } from '../db/company';

const COLORS = {
    navy: '#173659',
    red: '#dc2626',
    amber: '#f59e0b',
    green: '#16a34a',
    blue: '#2563eb',
    grey: '#64748b',
    lightRed: '#fef2f2',
    lightAmber: '#fffbeb',
    lightGreen: '#f0fdf4',
    lightBlue: '#eff6ff',
    lightGrey: '#f8fafc',
    border: '#e2e8f0',
};

const formatDateShort = (d) => {
    if (!d) return '—';
    try {
        const dt = new Date(d);
        return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
    } catch { return d; }
};

const DataDiagnostics = () => {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [rawData, setRawData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchFilter, setSearchFilter] = useState('');
    const [selectedTab, setSelectedTab] = useState(null);
    const [expandedIssue, setExpandedIssue] = useState(null);
    const [showAllRows, setShowAllRows] = useState(false);
    const [selectedType, setSelectedType] = useState(null);

    // Load raw (un-deduped) data directly — skips company.js dedup
    useEffect(() => {
        if (!selectedJob || !selectedVersion) return;
        setLoading(true);
        loadUserDataRaw({ id: selectedJob, forecastGuid: selectedVersion })
            .then(response => setRawData(response?.data || []))
            .catch(err => { console.error('dataDiagnostics raw load error:', err); setRawData([]); })
            .finally(() => setLoading(false));
    }, [selectedJob, selectedVersion]);

    // Filter data for current job + version
    const currentData = useMemo(() => {
        if (!rawData || !selectedJob || !selectedVersion) return [];
        return rawData.filter(row =>
            (row.BU_ID || '').toString() == selectedJob.toString() &&
            (row.Version_GUID || '').toString() == selectedVersion.toString()
        );
    }, [rawData, selectedJob, selectedVersion]);

    // Group by Forecast_Tab
    const tabGroups = useMemo(() => {
        const groups = {};
        currentData.forEach(row => {
            const tab = row.Forecast_Tab || '(No Tab)';
            if (!groups[tab]) groups[tab] = [];
            groups[tab].push(row);
        });
        return groups;
    }, [currentData]);

    const tabNames = useMemo(() => Object.keys(tabGroups).sort(), [tabGroups]);

    // ─── DIAGNOSTICS ENGINE ───

    const diagnostics = useMemo(() => {
        const issues = [];

        // 1. Find exact duplicates (same key fields) — now on RAW data so we see real DB dupes
        const keyMap = {};
        currentData.forEach((row, idx) => {
            const key = `${row.Forecast_Field_Name}__${row.Forecast_Tab}__${row.Forecast_Row_ID}`;
            if (!keyMap[key]) keyMap[key] = [];
            keyMap[key].push({ ...row, _idx: idx });
        });
        const duplicateGroups = Object.entries(keyMap).filter(([, rows]) => rows.length > 1);
        duplicateGroups.forEach(([key, rows]) => {
            // Sort by most recent first so we can see which would be kept vs dropped by dedup
            rows.sort((a, b) => {
                const aDate = new Date(Math.max(new Date(a.DSS_Created_Date || 0), new Date(a.DSS_Updated_Datetime || 0)));
                const bDate = new Date(Math.max(new Date(b.DSS_Created_Date || 0), new Date(b.DSS_Updated_Datetime || 0)));
                return bDate - aDate;
            });
            rows.forEach((r, i) => { r._dedupStatus = i === 0 ? 'KEPT' : 'DROPPED'; });
            issues.push({
                type: 'duplicate',
                severity: 'error',
                tab: rows[0].Forecast_Tab,
                title: `Duplicate rows (${rows.length}x) — RowID: ${rows[0].Forecast_Row_ID || '(none)'}`,
                description: `Key: Field="${rows[0].Forecast_Field_Name}", RowID="${rows[0].Forecast_Row_ID}", Tab="${rows[0].Forecast_Tab}" — oldest: ${formatDateShort(rows[rows.length - 1].DSS_Created_Date)}, newest: ${formatDateShort(rows[0].DSS_Created_Date)}`,
                rows,
            });
        });

        // 1b. Duplicates with DIFFERENT Forecast_Value — most concerning
        duplicateGroups.forEach(([key, rows]) => {
            const uniqueFV = new Set(rows.map(r => pNum(r.Forecast_Value)));
            if (uniqueFV.size > 1) {
                const vals = rows.map(r => pNum(r.Forecast_Value));
                const spread = Math.max(...vals) - Math.min(...vals);
                issues.push({
                    type: 'value-mismatch',
                    severity: 'error',
                    tab: rows[0].Forecast_Tab,
                    title: `Forecast_Value mismatch (${uniqueFV.size} values) — RowID: ${rows[0].Forecast_Row_ID || '(none)'}`,
                    description: `Field="${rows[0].Forecast_Field_Name}", Tab="${rows[0].Forecast_Tab}" — values: ${[...uniqueFV].map(v => fNum(Math.round(v), 1)).join(' vs ')} (spread: ${fNum(Math.round(spread), 1)})`,
                    rows: rows.map(r => ({ ...r })),
                });
            }
        });

        // 2. Completion date tracking — find rows with Project_End or Phase_End, group by RowID to see changes
        const completionMap = {};
        currentData.forEach(row => {
            if (!row.Project_End && !row.Phase_End) return;
            const key = `${row.Forecast_Tab}__${row.Forecast_Row_ID || row.Row_Label || '(no id)'}`;
            if (!completionMap[key]) completionMap[key] = [];
            completionMap[key].push(row);
        });
        Object.entries(completionMap).forEach(([key, rows]) => {
            const uniqueProjectEnds = new Set(rows.map(r => r.Project_End ? new Date(r.Project_End).toDateString() : null).filter(Boolean));
            const uniquePhaseEnds = new Set(rows.map(r => r.Phase_End ? new Date(r.Phase_End).toDateString() : null).filter(Boolean));
            const hasConflicts = uniqueProjectEnds.size > 1 || uniquePhaseEnds.size > 1;
            if (rows.length > 1 || hasConflicts) {
                issues.push({
                    type: 'completion-date',
                    severity: hasConflicts ? 'error' : 'info',
                    tab: rows[0].Forecast_Tab,
                    title: `Completion date${hasConflicts ? ' CONFLICT' : ''}: ${rows[0].Forecast_Row_ID || rows[0].Row_Label || '(no id)'}`,
                    description: hasConflicts
                        ? `${uniqueProjectEnds.size > 1 ? `Project_End has ${uniqueProjectEnds.size} different values. ` : ''}${uniquePhaseEnds.size > 1 ? `Phase_End has ${uniquePhaseEnds.size} different values.` : ''}`
                        : `${rows.length} record(s) — Project_End: ${rows[0].Project_End ? formatDateShort(rows[0].Project_End) : '—'}, Phase_End: ${rows[0].Phase_End ? formatDateShort(rows[0].Phase_End) : '—'}`,
                    rows,
                });
            }
        });

        // 3. Missing Cost_Type or Cost_Code where they should be filled
        // Only flag in tabs that already have cost data on some rows
        const costTabs = currentData.reduce((s, r) => {
            if (r.Cost_Type || r.Cost_Code) s.add(r.Forecast_Tab);
            return s;
        }, new Set());
        const missingCost = currentData.filter(row => {
            if (!costTabs.has(row.Forecast_Tab)) return false;
            const hasValue = pNum(row.Value_Amount) !== 0 || pNum(row.Forecast_Value) !== 0 || pNum(row.Number_Hours) !== 0;
            if (!hasValue) return false;
            return !row.Cost_Type || !row.Cost_Code;
        });
        if (missingCost.length > 0) {
            const byTab = {};
            missingCost.forEach(row => {
                const tab = row.Forecast_Tab || '(No Tab)';
                if (!byTab[tab]) byTab[tab] = [];
                byTab[tab].push(row);
            });
            Object.entries(byTab).forEach(([tab, rows]) => {
                const missingType = rows.filter(r => !r.Cost_Type).length;
                const missingCode = rows.filter(r => !r.Cost_Code).length;
                issues.push({
                    type: 'missing-cost',
                    severity: 'warning',
                    tab,
                    title: `${rows.length} row(s) missing Cost Type/Code`,
                    description: `In "${tab}": ${missingType > 0 ? `${missingType} missing Cost_Type` : ''}${missingType > 0 && missingCode > 0 ? ', ' : ''}${missingCode > 0 ? `${missingCode} missing Cost_Code` : ''} — rows have values but no cost classification`,
                    rows,
                });
            });
        }

        // 4. Suspicious values — rates, amounts, or hours that look wrong
        const suspiciousRows = [];
        currentData.forEach(row => {
            const reasons = [];
            const rate = pNum(row.Rate);
            const hours = pNum(row.Number_Hours);
            const units = pNum(row.Number_Units);
            const value = pNum(row.Value_Amount);
            const forecastValue = pNum(row.Forecast_Value);

            if (hours > 0 && rate === 0 && value > 0) reasons.push('Hours set but Rate is 0');
            if (rate > 0 && hours === 0 && units === 0 && value > 0) reasons.push('Rate set but no Hours or Units');
            if (rate > 500 && hours > 0) reasons.push(`Unusually high rate: $${rate}/hr`);
            if (value < -1000000) reasons.push(`Very large negative value: ${value}`);
            if (hours < 0) reasons.push(`Negative hours: ${hours}`);
            if (value !== 0 && forecastValue !== 0) {
                const diff = Math.abs(value - forecastValue);
                const maxVal = Math.max(Math.abs(value), Math.abs(forecastValue));
                if (diff > 100 && diff / maxVal > 0.1) {
                    reasons.push(`Value_Amount (${Math.round(value)}) ≠ Forecast_Value (${Math.round(forecastValue)}) — ${Math.round(diff / maxVal * 100)}% off`);
                }
            }
            if (value === 0 && forecastValue === 0 && (hours > 10 || units > 10)) {
                reasons.push(`Has ${hours > 0 ? hours + ' hours' : ''}${hours > 0 && units > 0 ? ' and ' : ''}${units > 0 ? units + ' units' : ''} but $0 value`);
            }
            if (reasons.length > 0) suspiciousRows.push({ ...row, _reasons: reasons });
        });
        if (suspiciousRows.length > 0) {
            const byTab = {};
            suspiciousRows.forEach(row => {
                const tab = row.Forecast_Tab || '(No Tab)';
                if (!byTab[tab]) byTab[tab] = [];
                byTab[tab].push(row);
            });
            Object.entries(byTab).forEach(([tab, rows]) => {
                issues.push({
                    type: 'suspicious-value',
                    severity: 'warning',
                    tab,
                    title: `${rows.length} row(s) with suspicious values`,
                    description: `In "${tab}": ${[...new Set(rows.flatMap(r => r._reasons))].slice(0, 3).join('; ')}${rows.length > 3 ? '...' : ''}`,
                    rows,
                });
            });
        }

        // 5. Same RowID + Tab has duplicate field entries (pre-dedup raw data)
        const rowIdTabGroups = {};
        currentData.forEach(row => {
            if (!row.Forecast_Row_ID) return;
            const key = `${row.Forecast_Tab}__${row.Forecast_Row_ID}`;
            if (!rowIdTabGroups[key]) rowIdTabGroups[key] = { fields: {}, rows: [] };
            const fn = row.Forecast_Field_Name || '';
            if (!rowIdTabGroups[key].fields[fn]) rowIdTabGroups[key].fields[fn] = 0;
            rowIdTabGroups[key].fields[fn]++;
            rowIdTabGroups[key].rows.push(row);
        });
        Object.entries(rowIdTabGroups).forEach(([key, data]) => {
            const dupeFields = Object.entries(data.fields).filter(([, count]) => count > 1);
            if (dupeFields.length > 0) {
                const tab = data.rows[0].Forecast_Tab;
                issues.push({
                    type: 'field-duplicate',
                    severity: 'error',
                    tab,
                    title: `RowID "${data.rows[0].Forecast_Row_ID}" has duplicate field entries`,
                    description: `Fields duplicated: ${dupeFields.map(([f, c]) => `${f} (${c}x)`).join(', ')} in "${tab}"`,
                    rows: data.rows,
                });
            }
        });

        return issues;
    }, [currentData]);

    // Filter diagnostics
    const filteredIssues = useMemo(() => {
        let filtered = diagnostics;
        if (selectedTab) filtered = filtered.filter(i => i.tab === selectedTab);
        if (selectedType) filtered = filtered.filter(i => i.type === selectedType);
        if (searchFilter) {
            const s = searchFilter.toLowerCase();
            filtered = filtered.filter(i =>
                i.title.toLowerCase().includes(s) ||
                i.description.toLowerCase().includes(s) ||
                i.type.toLowerCase().includes(s)
            );
        }
        // Sort value-mismatch items to top
        filtered.sort((a, b) => {
            const aVM = a.type === 'value-mismatch' ? 0 : 1;
            const bVM = b.type === 'value-mismatch' ? 0 : 1;
            if (aVM !== bVM) return aVM - bVM;
            // Then by severity: error > warning > info
            const sevOrder = { error: 0, warning: 1, info: 2 };
            return (sevOrder[a.severity] || 3) - (sevOrder[b.severity] || 3);
        });
        return filtered;
    }, [diagnostics, selectedTab, selectedType, searchFilter]);

    // Issue type counts for filter tabs
    const typeCounts = useMemo(() => {
        const counts = {};
        diagnostics.forEach(i => {
            counts[i.type] = (counts[i.type] || 0) + 1;
        });
        return counts;
    }, [diagnostics]);

    // Summary counts
    const summary = useMemo(() => {
        const errors = diagnostics.filter(i => i.severity === 'error').length;
        const warnings = diagnostics.filter(i => i.severity === 'warning').length;
        const infos = diagnostics.filter(i => i.severity === 'info').length;
        const totalRows = currentData.length;
        const totalDupeRows = diagnostics.filter(i => i.type === 'duplicate').reduce((s, i) => s + i.rows.length, 0);
        const valueMismatches = diagnostics.filter(i => i.type === 'value-mismatch').length;
        const completionIssues = diagnostics.filter(i => i.type === 'completion-date').length;
        const missingCostRows = diagnostics.filter(i => i.type === 'missing-cost').reduce((s, i) => s + i.rows.length, 0);
        return { errors, warnings, infos, totalRows, totalDupeRows, valueMismatches, completionIssues, missingCostRows };
    }, [diagnostics, currentData]);

    // Data for the selected tab's raw browser
    const tabData = useMemo(() => {
        if (!selectedTab || !tabGroups[selectedTab]) return [];
        const data = tabGroups[selectedTab];
        if (searchFilter) {
            const s = searchFilter.toLowerCase();
            return data.filter(row =>
                Object.values(row).some(v => v && v.toString().toLowerCase().includes(s))
            );
        }
        return data;
    }, [selectedTab, tabGroups, searchFilter]);

    const severityIcon = (sev) => {
        switch (sev) {
            case 'error': return '🔴';
            case 'warning': return '🟡';
            case 'info': return '🔵';
            default: return '⚪';
        }
    };

    const typeIcon = (type) => {
        switch (type) {
            case 'duplicate': return '📋';
            case 'value-mismatch': return '⚡';
            case 'completion-date': return '📅';
            case 'missing-cost': return '🏷️';
            case 'suspicious-value': return '⚠️';
            case 'field-duplicate': return '🔁';
            default: return '❓';
        }
    };

    const fieldColumns = [
        'Forecast_Tab', 'Forecast_Field_Name', 'Forecast_Row_ID', 'Row_Label',
        'Value_Amount', 'Forecast_Value', 'Number_Hours', 'Number_Units', 'Rate',
        'Cost_Type', 'Cost_Code', 'Status',
        'Project_End', 'Phase_End',
        'DSS_Created_Date', 'DSS_Updated_Datetime', 'Forecast_User_Data_GUID',
    ];

    const cellStyle = {
        fontSize: 11,
        padding: '3px 6px',
        borderRight: '1px solid #e2e8f0',
        borderBottom: '1px solid #e2e8f0',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: 180,
    };

    if (!selectedJob) {
        return (
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
                <Text style={{ fontSize: 16, color: '#666' }}>Select a job to view data diagnostics.</Text>
            </View>
        );
    }

    return (
        <View style={{ flex: 1, backgroundColor: '#f8fafc' }}>
            <ScrollView style={{ flex: 1 }}>
                <View style={{ padding: 20, maxWidth: 1600 }}>
                    {/* Header */}
                    <Text style={{ fontSize: 20, fontWeight: '800', color: COLORS.navy, marginBottom: 4 }}>
                        Data Diagnostics (Raw — No Dedup)
                    </Text>
                    <Text style={{ fontSize: 12, color: COLORS.grey, marginBottom: 16 }}>
                        {loading ? 'Loading raw data...' : `${currentData.length} raw rows for Job ${selectedJob}, Version ${selectedVersion} — dedup is skipped to expose duplicate sources`}
                    </Text>

                    {/* Summary Cards */}
                    <View style={{ flexDirection: 'row', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                        <SummaryCard label="Raw Rows" value={summary.totalRows} color={COLORS.blue} bg={COLORS.lightBlue} />
                        <SummaryCard label="Tabs" value={tabNames.length} color={COLORS.blue} bg={COLORS.lightBlue} />
                        <SummaryCard label="Duplicate Rows" value={summary.totalDupeRows} color={summary.totalDupeRows > 0 ? COLORS.red : COLORS.green} bg={summary.totalDupeRows > 0 ? COLORS.lightRed : COLORS.lightGreen} />
                        <SummaryCard label="Value Mismatches" value={summary.valueMismatches} color={summary.valueMismatches > 0 ? COLORS.red : COLORS.green} bg={summary.valueMismatches > 0 ? COLORS.lightRed : COLORS.lightGreen} />
                        <SummaryCard label="Completion Issues" value={summary.completionIssues} color={summary.completionIssues > 0 ? COLORS.amber : COLORS.green} bg={summary.completionIssues > 0 ? COLORS.lightAmber : COLORS.lightGreen} />
                        <SummaryCard label="Missing Cost Type/Code" value={summary.missingCostRows} color={summary.missingCostRows > 0 ? COLORS.amber : COLORS.green} bg={summary.missingCostRows > 0 ? COLORS.lightAmber : COLORS.lightGreen} />
                        <SummaryCard label="Errors" value={summary.errors} color={COLORS.red} bg={COLORS.lightRed} />
                        <SummaryCard label="Warnings" value={summary.warnings} color={COLORS.amber} bg={COLORS.lightAmber} />
                    </View>

                    {/* Tab Breakdown */}
                    <View style={{
                        backgroundColor: '#fff', borderRadius: 6, border: '1px solid #e2e8f0',
                        marginBottom: 16, overflow: 'hidden',
                    }}>
                        <View style={{ padding: '10px 14px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                            <Text style={{ fontSize: 13, fontWeight: '700', color: '#334155' }}>Rows by Tab</Text>
                        </View>
                        <View style={{ padding: 8, flexDirection: 'row', flexWrap: 'wrap', gap: 4 }}>
                            <Pressable
                                onPress={() => setSelectedTab(null)}
                                style={{
                                    paddingHorizontal: 10, paddingVertical: 4, borderRadius: 4,
                                    backgroundColor: !selectedTab ? COLORS.navy : '#f1f5f9',
                                    border: '1px solid ' + (!selectedTab ? COLORS.navy : '#cbd5e1'),
                                }}
                            >
                                <Text style={{ fontSize: 11, fontWeight: '600', color: !selectedTab ? '#fff' : '#475569' }}>
                                    All ({currentData.length})
                                </Text>
                            </Pressable>
                            {tabNames.map(tab => {
                                const count = tabGroups[tab].length;
                                const hasIssues = diagnostics.some(i => i.tab === tab && i.severity === 'error');
                                return (
                                    <Pressable
                                        key={tab}
                                        onPress={() => setSelectedTab(selectedTab === tab ? null : tab)}
                                        style={{
                                            paddingHorizontal: 10, paddingVertical: 4, borderRadius: 4,
                                            backgroundColor: selectedTab === tab ? COLORS.navy : '#f1f5f9',
                                            border: '1px solid ' + (selectedTab === tab ? COLORS.navy : hasIssues ? COLORS.red : '#cbd5e1'),
                                        }}
                                    >
                                        <Text style={{ fontSize: 11, fontWeight: '600', color: selectedTab === tab ? '#fff' : hasIssues ? COLORS.red : '#475569' }}>
                                            {tab} ({count}){hasIssues ? ' ⚠' : ''}
                                        </Text>
                                    </Pressable>
                                );
                            })}
                        </View>
                    </View>

                    {/* Issue Type Filter */}
                    <View style={{
                        backgroundColor: '#fff', borderRadius: 6, border: '1px solid #e2e8f0',
                        marginBottom: 16, overflow: 'hidden',
                    }}>
                        <View style={{ padding: '10px 14px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                            <Text style={{ fontSize: 13, fontWeight: '700', color: '#334155' }}>Filter by Issue Type</Text>
                        </View>
                        <View style={{ padding: 8, flexDirection: 'row', flexWrap: 'wrap', gap: 4 }}>
                            <Pressable
                                onPress={() => setSelectedType(null)}
                                style={{
                                    padding: '4px 10px', borderRadius: 4,
                                    backgroundColor: !selectedType ? COLORS.navy : '#f1f5f9',
                                    border: '1px solid ' + (!selectedType ? COLORS.navy : '#cbd5e1'),
                                }}
                            >
                                <Text style={{ fontSize: 11, fontWeight: '600', color: !selectedType ? '#fff' : '#475569' }}>
                                    All ({diagnostics.length})
                                </Text>
                            </Pressable>
                            {[{ key: 'value-mismatch', label: '⚡ Value Mismatches', color: COLORS.red },
                              { key: 'duplicate', label: '📋 Duplicates', color: COLORS.red },
                              { key: 'field-duplicate', label: '🔁 Field Dupes', color: COLORS.red },
                              { key: 'completion-date', label: '📅 Completion Dates', color: COLORS.amber },
                              { key: 'missing-cost', label: '🏷️ Missing Cost', color: COLORS.amber },
                              { key: 'suspicious-value', label: '⚠️ Suspicious Values', color: COLORS.amber },
                            ].filter(t => typeCounts[t.key]).map(t => (
                                <Pressable
                                    key={t.key}
                                    onPress={() => setSelectedType(selectedType === t.key ? null : t.key)}
                                    style={{
                                        padding: '4px 10px', borderRadius: 4,
                                        backgroundColor: selectedType === t.key ? COLORS.navy : '#f1f5f9',
                                        border: '1px solid ' + (selectedType === t.key ? COLORS.navy : t.key === 'value-mismatch' ? COLORS.red : '#cbd5e1'),
                                    }}
                                >
                                    <Text style={{ fontSize: 11, fontWeight: '600', color: selectedType === t.key ? '#fff' : t.key === 'value-mismatch' ? COLORS.red : '#475569' }}>
                                        {t.label} ({typeCounts[t.key]})
                                    </Text>
                                </Pressable>
                            ))}
                        </View>
                    </View>

                    {/* Search */}
                    <View style={{ marginBottom: 16 }}>
                        <TextInput
                            style={{
                                borderWidth: 1, borderColor: '#d1d5db', borderRadius: 6,
                                padding: '8px 12px', fontSize: 13, backgroundColor: '#fff',
                                maxWidth: 400,
                            }}
                            value={searchFilter}
                            onChangeText={setSearchFilter}
                            placeholder="Search issues or data..."
                        />
                    </View>

                    {/* Issues List */}
                    <View style={{
                        backgroundColor: '#fff', borderRadius: 6, border: '1px solid #e2e8f0',
                        marginBottom: 16, overflow: 'hidden',
                    }}>
                        <View style={{ padding: '10px 14px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc', flexDirection: 'row', justifyContent: 'space-between' }}>
                            <Text style={{ fontSize: 13, fontWeight: '700', color: '#334155' }}>
                                Issues ({filteredIssues.length})
                            </Text>
                        </View>
                        {filteredIssues.length === 0 ? (
                            <View style={{ padding: 20, alignItems: 'center' }}>
                                <Text style={{ fontSize: 13, color: COLORS.green, fontWeight: '600' }}>
                                    {diagnostics.length === 0 ? '✓ No issues found' : '✓ No issues match filter'}
                                </Text>
                            </View>
                        ) : (
                            filteredIssues.map((issue, idx) => (
                                <Pressable
                                    key={idx}
                                    onPress={() => setExpandedIssue(expandedIssue === idx ? null : idx)}
                                    style={{
                                        borderBottom: '1px solid #f1f5f9',
                                        backgroundColor: expandedIssue === idx ? '#f8fafc' : '#fff',
                                    }}
                                >
                                    <View style={{ flexDirection: 'row', alignItems: 'center', padding: '8px 14px', gap: 8 }}>
                                        <Text style={{ fontSize: 14 }}>{severityIcon(issue.severity)}</Text>
                                        <Text style={{ fontSize: 14 }}>{typeIcon(issue.type)}</Text>
                                        <View style={{ flex: 1 }}>
                                            <Text style={{ fontSize: 12, fontWeight: '600', color: '#1e293b' }}>
                                                {issue.title}
                                            </Text>
                                            <Text style={{ fontSize: 11, color: '#64748b' }}>
                                                {issue.description}
                                            </Text>
                                        </View>
                                        <View style={{
                                            backgroundColor: '#f1f5f9', paddingHorizontal: 8, paddingVertical: 2,
                                            borderRadius: 3,
                                        }}>
                                            <Text style={{ fontSize: 10, color: '#475569', fontWeight: '600' }}>
                                                {issue.tab}
                                            </Text>
                                        </View>
                                        <Text style={{ fontSize: 11, color: '#94a3b8' }}>
                                            {issue.rows.length} row(s) ▾
                                        </Text>
                                    </View>
                                    {expandedIssue === idx && (
                                        <View style={{ padding: '0 14px 10px', overflowX: 'auto' }}>
                                            {/* Show reasons for suspicious values */}
                                            {issue.type === 'suspicious-value' && (
                                                <View style={{ marginBottom: 6, padding: 6, backgroundColor: '#fffbeb', borderRadius: 4, border: '1px solid #fde68a' }}>
                                                    {issue.rows.slice(0, 10).map((row, rIdx) => (
                                                        <Text key={rIdx} style={{ fontSize: 11, color: '#92400e', marginBottom: 2 }}>
                                                            • RowID {row.Forecast_Row_ID || '(none)'}: {(row._reasons || []).join(', ')}
                                                        </Text>
                                                    ))}
                                                </View>
                                            )}
                                            <table style={{ borderCollapse: 'collapse', fontSize: 11, width: '100%' }}>
                                                <thead>
                                                    <tr style={{ backgroundColor: '#f1f5f9' }}>
                                                        {(issue.type === 'duplicate' || issue.type === 'value-mismatch') && (
                                                            <th style={{ ...cellStyle, fontWeight: '700', color: '#475569', fontSize: 10, textAlign: 'left' }}>
                                                                {issue.type === 'value-mismatch' ? 'Forecast_Value' : 'Dedup'}
                                                            </th>
                                                        )}
                                                        {fieldColumns.map(col => (
                                                            <th key={col} style={{ ...cellStyle, fontWeight: '700', color: '#475569', fontSize: 10, textAlign: 'left' }}>
                                                                {col.replace('Forecast_', '').replace('DSS_', '')}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {issue.rows.slice(0, 50).map((row, rIdx) => (
                                                        <tr key={rIdx} style={{
                                                            backgroundColor: row._dedupStatus === 'DROPPED' ? '#fef2f2' : rIdx % 2 === 0 ? '#fff' : '#fafafa',
                                                        }}>
                                                            {(issue.type === 'duplicate' || issue.type === 'value-mismatch') && (
                                                                <td style={{
                                                                    ...cellStyle,
                                                                    fontWeight: '700',
                                                                    color: issue.type === 'value-mismatch' ? COLORS.red : (row._dedupStatus === 'KEPT' ? COLORS.green : COLORS.red),
                                                                    fontSize: 10,
                                                                }}>
                                                                    {issue.type === 'value-mismatch' ? fNum(Math.round(pNum(row.Forecast_Value)), 1) : row._dedupStatus}
                                                                </td>
                                                            )}
                                                            {fieldColumns.map(col => (
                                                                <td key={col} style={{
                                                                    ...cellStyle,
                                                                    color: (col === 'Cost_Type' || col === 'Cost_Code') && !row[col] && issue.type === 'missing-cost' ? COLORS.red : undefined,
                                                                    fontWeight: (col === 'Project_End' || col === 'Phase_End') && issue.type === 'completion-date' && row[col] ? '700' : undefined,
                                                                }} title={row[col]?.toString() || ''}>
                                                                    {col.includes('Date') || col.includes('Datetime') || col === 'Project_End' || col === 'Phase_End'
                                                                        ? formatDateShort(row[col])
                                                                        : (row[col]?.toString() || '—')}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            {issue.rows.length > 50 && (
                                                <Text style={{ fontSize: 11, color: '#94a3b8', padding: 4 }}>
                                                    ...and {issue.rows.length - 50} more rows
                                                </Text>
                                            )}
                                        </View>
                                    )}
                                </Pressable>
                            ))
                        )}
                    </View>

                    {/* Completion Date Tracker */}
                    <View style={{
                        backgroundColor: '#fff', borderRadius: 6, border: '1px solid #e2e8f0',
                        marginBottom: 16, overflow: 'hidden',
                    }}>
                        <View style={{ padding: '10px 14px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                            <Text style={{ fontSize: 13, fontWeight: '700', color: '#334155' }}>Completion Date Tracker</Text>
                        </View>
                        <View style={{ overflowX: 'auto' }}>
                            <CompletionDateTable data={currentData} cellStyle={cellStyle} />
                        </View>
                    </View>

                    {/* Raw Data Browser for selected tab */}
                    {selectedTab && (
                        <View style={{
                            backgroundColor: '#fff', borderRadius: 6, border: '1px solid #e2e8f0',
                            marginBottom: 16, overflow: 'hidden',
                        }}>
                            <View style={{ padding: '10px 14px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text style={{ fontSize: 13, fontWeight: '700', color: '#334155' }}>
                                    Raw Data: {selectedTab} ({tabData.length} rows)
                                </Text>
                                <Pressable
                                    onPress={() => setShowAllRows(!showAllRows)}
                                    style={{ paddingHorizontal: 10, paddingVertical: 4, borderRadius: 4, backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1' }}
                                >
                                    <Text style={{ fontSize: 11, color: '#475569' }}>
                                        {showAllRows ? 'Show first 100' : `Show all ${tabData.length}`}
                                    </Text>
                                </Pressable>
                            </View>
                            <View style={{ overflowX: 'auto', padding: '0 0 10px' }}>
                                <table style={{ borderCollapse: 'collapse', fontSize: 11, width: '100%' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f1f5f9' }}>
                                            <th style={{ ...cellStyle, fontWeight: '700', color: '#475569', fontSize: 10, textAlign: 'left' }}>#</th>
                                            {fieldColumns.map(col => (
                                                <th key={col} style={{ ...cellStyle, fontWeight: '700', color: '#475569', fontSize: 10, textAlign: 'left' }}>
                                                    {col.replace('Forecast_', '').replace('DSS_', '')}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(showAllRows ? tabData : tabData.slice(0, 100)).map((row, rIdx) => (
                                            <tr key={rIdx} style={{ backgroundColor: rIdx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                                <td style={{ ...cellStyle, color: '#94a3b8' }}>{rIdx + 1}</td>
                                                {fieldColumns.map(col => (
                                                    <td key={col} style={cellStyle} title={row[col]?.toString() || ''}>
                                                        {col.includes('Date') || col.includes('Datetime') || col === 'Project_End' || col === 'Phase_End'
                                                            ? formatDateShort(row[col])
                                                            : (row[col]?.toString() || '—')}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {!showAllRows && tabData.length > 100 && (
                                    <Text style={{ fontSize: 11, color: '#94a3b8', padding: '6px 14px' }}>
                                        Showing first 100 of {tabData.length} rows
                                    </Text>
                                )}
                            </View>
                        </View>
                    )}

                    {/* Tab-level sum analysis */}
                    <View style={{
                        backgroundColor: '#fff', borderRadius: 6, border: '1px solid #e2e8f0',
                        marginBottom: 16, overflow: 'hidden',
                    }}>
                        <View style={{ padding: '10px 14px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                            <Text style={{ fontSize: 13, fontWeight: '700', color: '#334155' }}>Value Sums by Tab (Raw Data)</Text>
                        </View>
                        <View style={{ overflowX: 'auto' }}>
                            <table style={{ borderCollapse: 'collapse', fontSize: 11, width: '100%' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f1f5f9' }}>
                                        <th style={{ ...cellStyle, fontWeight: '700', textAlign: 'left' }}>Tab</th>
                                        <th style={{ ...cellStyle, fontWeight: '700', textAlign: 'right' }}>Rows</th>
                                        <th style={{ ...cellStyle, fontWeight: '700', textAlign: 'right' }}>Dupes</th>
                                        <th style={{ ...cellStyle, fontWeight: '700', textAlign: 'right' }}>Σ Value_Amount</th>
                                        <th style={{ ...cellStyle, fontWeight: '700', textAlign: 'right' }}>Σ Forecast_Value</th>
                                        <th style={{ ...cellStyle, fontWeight: '700', textAlign: 'right' }}>Σ Hours</th>
                                        <th style={{ ...cellStyle, fontWeight: '700', textAlign: 'right' }}>Missing Cost</th>
                                        <th style={{ ...cellStyle, fontWeight: '700', textAlign: 'right' }}>Has Completion</th>
                                        <th style={{ ...cellStyle, fontWeight: '700', textAlign: 'left' }}>Last Updated</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tabNames.map((tab, idx) => {
                                        const rows = tabGroups[tab];
                                        const sumVal = rows.reduce((s, r) => s + pNum(r.Value_Amount), 0);
                                        const sumFV = rows.reduce((s, r) => s + pNum(r.Forecast_Value), 0);
                                        const sumHrs = rows.reduce((s, r) => s + pNum(r.Number_Hours), 0);
                                        const dupeCount = diagnostics.filter(i => i.type === 'duplicate' && i.tab === tab).reduce((s, i) => s + i.rows.length, 0);
                                        const missingCostCount = rows.filter(r => (!r.Cost_Type || !r.Cost_Code) && (pNum(r.Value_Amount) !== 0 || pNum(r.Forecast_Value) !== 0)).length;
                                        const hasCompletion = rows.filter(r => r.Project_End || r.Phase_End).length;
                                        const lastUpdated = rows.reduce((max, r) => {
                                            const d = new Date(r.DSS_Updated_Datetime || r.DSS_Created_Date || 0);
                                            return d > max ? d : max;
                                        }, new Date(0));

                                        return (
                                            <tr key={tab} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                                <td style={{ ...cellStyle, fontWeight: '600' }}>{tab}</td>
                                                <td style={{ ...cellStyle, textAlign: 'right' }}>{rows.length}</td>
                                                <td style={{ ...cellStyle, textAlign: 'right', color: dupeCount > 0 ? COLORS.red : COLORS.green, fontWeight: dupeCount > 0 ? '700' : '400' }}>{dupeCount}</td>
                                                <td style={{ ...cellStyle, textAlign: 'right' }}>{fNum(Math.round(sumVal), 1)}</td>
                                                <td style={{ ...cellStyle, textAlign: 'right' }}>{fNum(Math.round(sumFV), 1)}</td>
                                                <td style={{ ...cellStyle, textAlign: 'right' }}>{sumHrs ? fNum(Math.round(sumHrs), 1) : '—'}</td>
                                                <td style={{ ...cellStyle, textAlign: 'right', color: missingCostCount > 0 ? COLORS.amber : undefined }}>{missingCostCount > 0 ? missingCostCount : '—'}</td>
                                                <td style={{ ...cellStyle, textAlign: 'right' }}>{hasCompletion > 0 ? hasCompletion : '—'}</td>
                                                <td style={cellStyle}>{lastUpdated.getFullYear() > 2000 ? formatDateShort(lastUpdated) : '—'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </View>
                    </View>
                </View>
            </ScrollView>
        </View>
    );
};

// ─── Completion Date Tracker sub-component ─────────────────────────────────
const CompletionDateTable = ({ data, cellStyle }) => {
    const completionRows = useMemo(() => {
        const map = {};
        data.forEach(row => {
            if (!row.Project_End && !row.Phase_End) return;
            const key = `${row.Forecast_Tab}__${row.Forecast_Row_ID || row.Row_Label || ''}`;
            if (!map[key]) map[key] = { tab: row.Forecast_Tab, rowId: row.Forecast_Row_ID, label: row.Row_Label, entries: [] };
            map[key].entries.push({
                projectEnd: row.Project_End,
                phaseEnd: row.Phase_End,
                created: row.DSS_Created_Date,
                updated: row.DSS_Updated_Datetime,
                guid: row.Forecast_User_Data_GUID,
                fieldName: row.Forecast_Field_Name,
                value: row.Value_Amount,
            });
        });
        Object.values(map).forEach(g => {
            g.entries.sort((a, b) => {
                const aD = new Date(Math.max(new Date(a.created || 0), new Date(a.updated || 0)));
                const bD = new Date(Math.max(new Date(b.created || 0), new Date(b.updated || 0)));
                return bD - aD;
            });
            g.uniqueProjectEnds = new Set(g.entries.map(e => e.projectEnd ? new Date(e.projectEnd).toDateString() : null).filter(Boolean)).size;
            g.uniquePhaseEnds = new Set(g.entries.map(e => e.phaseEnd ? new Date(e.phaseEnd).toDateString() : null).filter(Boolean)).size;
            g.hasConflict = g.uniqueProjectEnds > 1 || g.uniquePhaseEnds > 1;
        });
        return Object.values(map).sort((a, b) => (b.hasConflict ? 1 : 0) - (a.hasConflict ? 1 : 0));
    }, [data]);

    if (completionRows.length === 0) {
        return (
            <View style={{ padding: 16, alignItems: 'center' }}>
                <Text style={{ fontSize: 12, color: '#94a3b8' }}>No rows with completion dates found</Text>
            </View>
        );
    }

    return (
        <table style={{ borderCollapse: 'collapse', fontSize: 11, width: '100%' }}>
            <thead>
                <tr style={{ backgroundColor: '#f1f5f9' }}>
                    <th style={{ ...cellStyle, fontWeight: '700', textAlign: 'left' }}>Tab</th>
                    <th style={{ ...cellStyle, fontWeight: '700', textAlign: 'left' }}>Row ID</th>
                    <th style={{ ...cellStyle, fontWeight: '700', textAlign: 'left' }}>Label</th>
                    <th style={{ ...cellStyle, fontWeight: '700', textAlign: 'right' }}>Records</th>
                    <th style={{ ...cellStyle, fontWeight: '700', textAlign: 'left' }}>Project_End</th>
                    <th style={{ ...cellStyle, fontWeight: '700', textAlign: 'right' }}>Changes</th>
                    <th style={{ ...cellStyle, fontWeight: '700', textAlign: 'left' }}>Phase_End</th>
                    <th style={{ ...cellStyle, fontWeight: '700', textAlign: 'right' }}>Changes</th>
                    <th style={{ ...cellStyle, fontWeight: '700', textAlign: 'left' }}>Last Updated</th>
                </tr>
            </thead>
            <tbody>
                {completionRows.map((group, idx) => {
                    const latest = group.entries[0];
                    return (
                        <tr key={idx} style={{
                            backgroundColor: group.hasConflict ? '#fef2f2' : idx % 2 === 0 ? '#fff' : '#fafafa',
                        }}>
                            <td style={cellStyle}>{group.tab}</td>
                            <td style={cellStyle}>{group.rowId || '—'}</td>
                            <td style={cellStyle}>{group.label || '—'}</td>
                            <td style={{ ...cellStyle, textAlign: 'right' }}>{group.entries.length}</td>
                            <td style={{ ...cellStyle, fontWeight: group.hasConflict ? '700' : undefined }}>
                                {latest.projectEnd ? formatDateShort(latest.projectEnd) : '—'}
                            </td>
                            <td style={{
                                ...cellStyle, textAlign: 'right',
                                color: group.uniqueProjectEnds > 1 ? COLORS.red : undefined,
                                fontWeight: group.uniqueProjectEnds > 1 ? '700' : undefined,
                            }}>
                                {group.uniqueProjectEnds > 1 ? `${group.uniqueProjectEnds} different` : group.uniqueProjectEnds === 1 ? '1' : '—'}
                            </td>
                            <td style={{ ...cellStyle, fontWeight: group.hasConflict ? '700' : undefined }}>
                                {latest.phaseEnd ? formatDateShort(latest.phaseEnd) : '—'}
                            </td>
                            <td style={{
                                ...cellStyle, textAlign: 'right',
                                color: group.uniquePhaseEnds > 1 ? COLORS.red : undefined,
                                fontWeight: group.uniquePhaseEnds > 1 ? '700' : undefined,
                            }}>
                                {group.uniquePhaseEnds > 1 ? `${group.uniquePhaseEnds} different` : group.uniquePhaseEnds === 1 ? '1' : '—'}
                            </td>
                            <td style={cellStyle}>{formatDateShort(latest.updated || latest.created)}</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};

const SummaryCard = ({ label, value, color, bg }) => (
    <View style={{
        backgroundColor: bg, border: `1px solid ${color}22`, borderRadius: 6,
        padding: '10px 16px', minWidth: 110,
    }}>
        <Text style={{ fontSize: 22, fontWeight: '800', color }}>{value}</Text>
        <Text style={{ fontSize: 11, color, fontWeight: '600' }}>{label}</Text>
    </View>
);

export default DataDiagnostics;
